/* MF - Scent Stack Scroll
   Scroll-scrubbed video + tier reveals.
   - Desktop AND mobile: rAF loop LERPs video.currentTime toward scroll
     target each frame, decoupling fast scroll inputs from the video so
     playback stays smooth even when keyframes are sparse.
   - Tiers fade in at phase-aligned scroll thresholds.
   - prefers-reduced-motion: tiers shown statically; video paused on
     last frame.
   - No-video case: tiers fade in via IntersectionObserver.
   ---------------------------------------------------------------- */

(function () {
  if (customElements.get('mf-scent-stack-scroll')) return;

  // How aggressively the displayed time eases toward the scroll target.
  // 1.0 = no easing (snaps instantly), 0.10 = very smooth/lagged.
  const LERP_FACTOR = 0.18;

  class MfScentStackScroll extends HTMLElement {
    connectedCallback() {
      this.wrap   = this.querySelector('.mfsss__wrap');
      this.pinned = this.querySelector('.mfsss__pinned');
      this.video  = this.querySelector('video.mfsss__video');
      this.tiers  = Array.from(this.querySelectorAll('.mfsss__tier'));

      // When true, scroll progress is monotonic — only moves forward.
      // Scrolling back up will NOT rewind the video or hide revealed tiers.
      // The user sees the full animation once on first scroll-down, then it
      // stays at the end frame for the rest of the page lifetime.
      this._forwardOnly  = this.getAttribute('data-forward-only') === 'true';
      this._maxProgress  = 0;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this._setupReducedMotion();
        return;
      }

      if (!this.video) {
        this._setupNoVideoFallback();
        return;
      }

      this._setupScrub();
    }

    disconnectedCallback() {
      if (this._scrollHandler) {
        window.removeEventListener('scroll', this._scrollHandler);
        window.removeEventListener('resize', this._scrollHandler);
      }
      if (this._io) this._io.disconnect();
      if (this._rafId) cancelAnimationFrame(this._rafId);
    }

    /* ---------------- Desktop scroll-scrub ---------------- */
    _setupScrub() {
      const v = this.video;
      v.muted = true;
      v.playsInline = true;
      v.loop = false;
      v.pause();

      this._targetT  = 0;   // where the scroll wants the video to be
      this._displayT = 0;   // where we've actually seeked to
      this._lastSeek = -1;  // last currentTime we actually set
      this._active   = false;
      this._primed   = false;

      const prime = () => {
        if (this._primed) return;
        const p = v.play();
        if (p && typeof p.then === 'function') {
          p.then(() => { v.pause(); v.currentTime = 0; this._primed = true; })
           .catch(() => {
             // Autoplay blocked (iOS Low Power, restrictive policies). Show
             // a tap affordance; first user gesture will prime + seek.
             this._primed = false;
             this.classList.add('mfsss--needs-tap');
             const onTap = () => {
               const pp = v.play();
               if (pp && typeof pp.then === 'function') {
                 pp.then(() => { v.pause(); this._primed = true; this.classList.remove('mfsss--needs-tap'); })
                   .catch(() => {});
               }
               this.removeEventListener('click', onTap);
               this.removeEventListener('touchstart', onTap);
             };
             this.addEventListener('click', onTap, { passive: true });
             this.addEventListener('touchstart', onTap, { passive: true });
           });
        } else {
          this._primed = true;
        }
      };

      const ready = () => {
        const io = new IntersectionObserver((entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              prime();
              this._active = true;
              if (this._rafId == null) this._rafId = requestAnimationFrame(this._tick.bind(this));
            } else {
              this._active = false;
            }
          });
        }, { rootMargin: '50% 0px 50% 0px', threshold: 0 });
        io.observe(this.wrap);
        this._io = io;

        this._scrollHandler = this._readScroll.bind(this);
        window.addEventListener('scroll', this._scrollHandler, { passive: true });
        window.addEventListener('resize', this._scrollHandler, { passive: true });
        this._readScroll();
      };

      if (v.readyState >= 1) ready();
      else v.addEventListener('loadedmetadata', ready, { once: true });
    }

    // Update the target time + tier visibility based on scroll position.
    // Cheap to call on every scroll event.
    _readScroll() {
      const rect = this.wrap.getBoundingClientRect();
      const total = this.wrap.offsetHeight - window.innerHeight;
      const scrolled = Math.max(0, Math.min(total, -rect.top));
      const rawProgress = total > 0 ? scrolled / total : 0;

      // Forward-only mode: clamp progress to the maximum seen so far so
      // scrolling back up doesn't rewind the video or hide revealed tiers.
      if (this._forwardOnly && rawProgress > this._maxProgress) {
        this._maxProgress = rawProgress;
      }
      const progress = this._forwardOnly ? this._maxProgress : rawProgress;

      // Once the animation completes for the first time (forward-only mode),
      // release the pinning behavior so the section behaves like a normal
      // flowing block on subsequent scrolls. CSS rules on `.mfsss.is-completed`
      // drop position:sticky from .mfsss__pinned and collapse the .mfsss__wrap
      // tall-scroll budget to natural content height.
      if (this._forwardOnly && !this._isCompleted && this._maxProgress >= 0.999) {
        this._isCompleted = true;
        this.classList.add('is-completed');
      }

      const v = this.video;
      if (v && v.duration && isFinite(v.duration)) {
        this._targetT = progress * v.duration;
      }

      const n = this.tiers.length;
      if (n > 0) {
        // Phase-aligned thresholds for the package-opens video:
        //   0.0–10% scroll → REVEAL (closed package, no tier text yet)
        //   10–30% → TOP phase (peach skin, plum)
        //   30–62.5% → MID phase (orris)
        //   62.5–100% → BASE phase (white moss, musks, sandalwood)
        // We reveal each tier ~2% before its phase begins so the text catches up to the visual.
        // Override via data-tier-thresholds="8,28,60" on the section if a future video needs different timing.
        const fallback = (n === 3) ? [0.08, 0.28, 0.60] : null;
        let thresholds = fallback;
        const attr = this.getAttribute('data-tier-thresholds');
        if (attr) {
          const parsed = attr.split(',').map((v) => parseFloat(v) / 100).filter((v) => !isNaN(v));
          if (parsed.length === n) thresholds = parsed;
        }

        for (let i = 0; i < n; i++) {
          const threshold = thresholds ? thresholds[i] : (i / n) - 0.03;
          if (this._forwardOnly) {
            // Only ADD the visible class — never remove it, so revealed
            // tiers stay visible when the user scrolls back up.
            if (progress >= threshold) {
              this.tiers[i].classList.add('is-visible');
            }
          } else {
            this.tiers[i].classList.toggle('is-visible', progress >= threshold);
          }
        }
      }

      // Kick the rAF loop if it isn't running yet
      if (this._active && this._rafId == null) {
        this._rafId = requestAnimationFrame(this._tick.bind(this));
      }
    }

    // rAF loop: smoothly ease displayT toward targetT, only seeking when
    // the difference exceeds a quarter-frame to avoid hammering the
    // decoder with redundant seeks.
    _tick() {
      this._rafId = null;
      if (!this._active) return;

      const v = this.video;
      if (!v || !v.duration || !isFinite(v.duration)) return;

      const target = this._targetT;
      // LERP
      const delta = target - this._displayT;
      if (Math.abs(delta) < 0.001) {
        // Settled — stop the loop until scroll fires again
        return;
      }
      this._displayT += delta * LERP_FACTOR;

      // Only commit a seek if it's meaningfully different from the last
      // one we issued (avoids overwhelming the decoder).
      if (Math.abs(this._displayT - this._lastSeek) > 0.016) {
        try { v.currentTime = this._displayT; } catch (e) {}
        this._lastSeek = this._displayT;
      }

      this._rafId = requestAnimationFrame(this._tick.bind(this));
    }

    /* ---------------- No-video fallback ---------------- */
    // Used when the merchant hasn't set a video URL. Just fades the tiers
    // in as they enter the viewport.
    _setupNoVideoFallback() {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-visible');
            io.unobserve(e.target);
          }
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });

      this.tiers.forEach((t, i) => {
        t.style.transitionDelay = (i * 220) + 'ms';
        io.observe(t);
      });
      this._io = io;
    }

    /* ---------------- Reduced motion ---------------- */
    _setupReducedMotion() {
      const v = this.video;
      if (v) {
        v.muted = true;
        v.playsInline = true;
        v.loop = false;
        v.pause();
        const showLastFrame = () => {
          try { v.currentTime = Math.max(0, (v.duration || 0) - 0.05); } catch (e) {}
        };
        if (v.readyState >= 1) showLastFrame();
        else v.addEventListener('loadedmetadata', showLastFrame, { once: true });
      }
      this.tiers.forEach((t) => t.classList.add('is-visible'));
    }
  }

  customElements.define('mf-scent-stack-scroll', MfScentStackScroll);
})();
